import { useRef, useEffect, useCallback } from 'react'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import type { DotLottie } from '@lottiefiles/dotlottie-react'
import styled from 'styled-components'
import primarySparkleAnimation from '../../assets/Primary sparkle.lottie?url'

interface SparkleLoaderProps {
  /** Whether the animation should be playing */
  isAnimating: boolean
  /** Size of the loader in pixels */
  size?: number
  /** Optional className for additional styling */
  className?: string
}

const Container = styled.div<{ $size: number }>`
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transform: rotate(180deg);
  margin-right: -8px;
  margin-left: -8px;
  margin-bottom: -4px;
`

/**
 * SparkleLoader - A Lottie-based loading indicator that plays when animating
 * and freezes on the current frame when not animating, acting as a static icon.
 */
export function SparkleLoader({ isAnimating, size = 24, className }: SparkleLoaderProps) {
  const dotLottieRef = useRef<DotLottie | null>(null)

  const dotLottieRefCallback = useCallback((dotLottie: DotLottie | null) => {
    dotLottieRef.current = dotLottie
  }, [])

  // Control play/pause based on isAnimating prop changes
  useEffect(() => {
    const dotLottie = dotLottieRef.current
    if (!dotLottie) return undefined

    const syncPlayState = () => {
      if (isAnimating) {
        dotLottie.play()
      } else {
        dotLottie.pause()
      }
    }

    // If already loaded, sync immediately
    if (dotLottie.isLoaded) {
      syncPlayState()
      return undefined
    }

    // Wait for load, then sync
    dotLottie.addEventListener('load', syncPlayState)
    return () => {
      dotLottie.removeEventListener('load', syncPlayState)
    }
  }, [isAnimating])

  return (
    <Container $size={size} className={className}>
      <DotLottieReact
        src={primarySparkleAnimation}
        loop
        autoplay={isAnimating}
        dotLottieRefCallback={dotLottieRefCallback}
        style={{ width: '100%', height: '100%' }}
      />
    </Container>
  )
}
